import React, { useState, useEffect, useCallback } from 'react';
import {
    Drawer, Descriptions, Tabs, Table, Tag, Space, Button, Popconfirm,
    message, Spin, Row, Col, Statistic, Card, Empty
} from 'antd';
import {
    CheckCircleOutlined, StopOutlined, KeyOutlined, DollarOutlined
} from '@ant-design/icons';
import userService from '../services/userService';
import { formatMoney, PLATFORM_CURRENCY } from '../../shared/utils/money';
import { t } from '../../shared/i18n';
import LinkedAccounts from './users/LinkedAccounts';
import UserProducts from './users/UserProducts';

const UserDetailDrawer = ({ visible, user, onClose, onChanged }) => {
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState(null);
    const [balanceHistory, setBalanceHistory] = useState([]);
    const [activity, setActivity] = useState([]);
    const [apiKeys, setApiKeys] = useState([]);
    const [actionLoading, setActionLoading] = useState(false);

    const loadAll = useCallback(async (userId) => {
        setLoading(true);
        try {
            const [summaryRes, balanceRes, activityRes, keysRes] = await Promise.all([
                userService.getUserSummary(userId).catch(() => ({ success: false })),
                userService.getBalanceHistory(userId).catch(() => ({ success: false })),
                userService.getUserActivity(userId).catch(() => ({ success: false })),
                userService.getUserApiKeys(userId).catch(() => ({ success: false }))
            ]);
            if (summaryRes.success) setSummary(summaryRes.data);
            if (balanceRes.success) setBalanceHistory(balanceRes.data || []);
            if (activityRes.success) setActivity(activityRes.data || []);
            if (keysRes.success) setApiKeys(keysRes.data || []);
        } catch (error) {
            message.error('加载用户详情失败');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (visible && user?.id) {
            setSummary(null);
            setBalanceHistory([]);
            setActivity([]);
            setApiKeys([]);
            loadAll(user.id);
        }
    }, [visible, user, loadAll]);

    const handleActivate = async () => {
        setActionLoading(true);
        try {
            const res = await userService.activateUser(user.id);
            if (res.success) {
                message.success('用户已启用');
                onChanged && onChanged();
            } else {
                message.error(res.error || res.message || '启用失败');
            }
        } catch (e) {
            message.error('启用失败');
        } finally {
            setActionLoading(false);
        }
    };

    const handleSuspend = async () => {
        setActionLoading(true);
        try {
            const res = await userService.suspendUser(user.id, '管理员操作');
            if (res.success) {
                message.success('用户已停用');
                onChanged && onChanged();
            } else {
                message.error(res.error || res.message || '停用失败');
            }
        } catch (e) {
            message.error('停用失败');
        } finally {
            setActionLoading(false);
        }
    };

    const handleRevokeKey = async (keyId) => {
        try {
            const res = await userService.revokeApiKey(keyId);
            if (res.success) {
                message.success('API密钥已撤销');
                setApiKeys(prev => prev.filter(k => k.id !== keyId));
            } else {
                message.error(res.error || res.message || '撤销失败');
            }
        } catch (e) {
            message.error('撤销失败');
        }
    };

    const balanceColumns = [
        { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 170 },
        { title: '类型', dataIndex: 'type', key: 'type', width: 120, render: t => <Tag>{t}</Tag> },
        {
            title: '金额', dataIndex: 'amount', key: 'amount', width: 120,
            // amount 的存储符号在 2026-05-25 变过，按后端 signed_amount（按 type 定符号）展示
            render: (amount, record) => {
                const credit = ['recharge', 'refund', 'transfer_in', 'commission'].includes(record.type);
                const num = record.signed_amount != null
                    ? parseFloat(record.signed_amount) || 0
                    : (credit ? 1 : -1) * Math.abs(parseFloat(amount) || 0);
                const cur = user?.balance_currency || PLATFORM_CURRENCY;
                return <span style={{ color: num >= 0 ? '#3f8600' : '#cf1322' }}>{num >= 0 ? '+' : '-'}{formatMoney(Math.abs(num), cur)}</span>;
            }
        },
        { title: '余额', dataIndex: 'balance_after', key: 'balance_after', width: 120, render: v => v != null ? formatMoney(parseFloat(v) || 0, user?.balance_currency || PLATFORM_CURRENCY) : '-' },
        { title: '说明', dataIndex: 'description', key: 'description' }
    ];

    const activityColumns = [
        { title: '时间', dataIndex: 'created_at', key: 'created_at', width: 170 },
        { title: '动作', dataIndex: 'action', key: 'action', width: 160 },
        { title: 'IP', dataIndex: 'ip_address', key: 'ip_address', width: 140 },
        { title: '详情', dataIndex: 'description', key: 'description' }
    ];

    const apiKeyColumns = [
        { title: '名称', dataIndex: 'name', key: 'name' },
        { title: '描述', dataIndex: 'description', key: 'description' },
        { title: '最后使用', dataIndex: 'last_used_at', key: 'last_used_at', width: 170, render: v => v || '从未' },
        { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 170 },
        { title: '过期', dataIndex: 'expires_at', key: 'expires_at', width: 170, render: v => v || '永久' },
        {
            title: '操作', key: 'action', width: 100,
            render: (_, record) => (
                <Popconfirm title="确定撤销此API密钥？" onConfirm={() => handleRevokeKey(record.id)} okText="撤销" cancelText="取消">
                    <Button danger size="small" icon={<KeyOutlined />}>撤销</Button>
                </Popconfirm>
            )
        }
    ];

    const items = [
        {
            key: 'summary',
            label: '摘要',
            children: summary ? (
                <Row gutter={16}>
                    <Col xs={12} sm={6}><Card bordered={false}><Statistic title="订阅" value={summary.subscription_count || 0} /></Card></Col>
                    <Col xs={12} sm={6}><Card bordered={false}><Statistic title="订单" value={summary.order_count || 0} /></Card></Col>
                    <Col xs={12} sm={6}><Card bordered={false}><Statistic title="DNS区域" value={summary.dns_zone_count || 0} /></Card></Col>
                    <Col xs={12} sm={6}><Card bordered={false}><Statistic title="SSL订单" value={summary.ssl_order_count || 0} /></Card></Col>
                </Row>
            ) : <Empty />
        },
        {
            key: 'products',
            label: '产品',
            children: user ? <UserProducts key={user.id} userId={user.id} /> : null
        },
        {
            key: 'balance',
            label: <span><DollarOutlined /> 余额流水</span>,
            children: <Table columns={balanceColumns} dataSource={balanceHistory} rowKey="id" size="small" pagination={{ pageSize: 10 }} locale={{ emptyText: '暂无流水' }} />
        },
        {
            key: 'activity',
            label: '活动日志',
            children: <Table columns={activityColumns} dataSource={activity} rowKey={(r) => r.id || `${r.created_at}-${r.action}`} size="small" pagination={{ pageSize: 10 }} locale={{ emptyText: '暂无活动记录' }} />
        },
        {
            key: 'apiKeys',
            label: <span><KeyOutlined /> API密钥</span>,
            children: <Table columns={apiKeyColumns} dataSource={apiKeys} rowKey="id" size="small" pagination={false} locale={{ emptyText: '暂无API密钥' }} />
        },
        {
            key: 'linked',
            label: t('Linked accounts'),
            children: user ? <LinkedAccounts key={user.id} userId={user.id} onChanged={onChanged} /> : null
        }
    ];

    return (
        <Drawer
            title={user ? `用户详情: ${user.username || user.email}` : '用户详情'}
            open={visible}
            onClose={onClose}
            width={960}
            extra={
                <Space>
                    <Button
                        icon={<CheckCircleOutlined />}
                        onClick={handleActivate}
                        loading={actionLoading}
                        disabled={user?.status === 'active'}
                    >
                        启用
                    </Button>
                    <Popconfirm title="确定停用此用户？" onConfirm={handleSuspend} okText="停用" cancelText="取消">
                        <Button
                            danger
                            icon={<StopOutlined />}
                            loading={actionLoading}
                            disabled={user?.status === 'suspended'}
                        >
                            停用
                        </Button>
                    </Popconfirm>
                </Space>
            }
        >
            <Spin spinning={loading}>
                {user && (
                    <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
                        <Descriptions.Item label="用户名">{user.username}</Descriptions.Item>
                        <Descriptions.Item label="邮箱">{user.email}</Descriptions.Item>
                        <Descriptions.Item label="角色">{user.role === 'admin' ? '管理员' : '普通用户'}</Descriptions.Item>
                        <Descriptions.Item label="状态"><Tag>{user.status}</Tag></Descriptions.Item>
                        <Descriptions.Item label="余额">{formatMoney(parseFloat(user.balance) || 0, user.balance_currency || PLATFORM_CURRENCY)}</Descriptions.Item>
                        <Descriptions.Item label="注册时间">{user.created_at}</Descriptions.Item>
                    </Descriptions>
                )}
                <Tabs items={items} />
            </Spin>
        </Drawer>
    );
};

export default UserDetailDrawer;
